import React, { useEffect, useState } from 'react';
import { transactionsAPI } from '../services/api';

const initialResolveForm = {
  merchant: 'Costco',
  itemsText: 'KS ORG BNLS CHK BRST\nGV SWT CRM BUTR',
};

const initialSyncForm = {
  query: 'kirkland chicken',
  merchant: 'Costco',
  scope: 'shared',
  pageSize: 20,
};

const initialSeedForm = {
  canonicalName: 'Kirkland Organic Boneless Chicken Breast',
  brand: 'Kirkland Signature',
  merchant: 'Costco',
  scope: 'shared',
};

const initialAliasEditForm = {
  rawText: 'KS ORG BNLS CHK BRST',
  canonicalName: 'Kirkland Organic Boneless Chicken Breast',
  brand: 'Kirkland Signature',
  merchant: 'Costco',
};

const initialBootstrapForm = {
  scope: 'shared',
  includeOpenFoodFacts: true,
  merchants: {
    Costco: true,
    Walmart: true,
    Target: true,
    Aldi: true,
    Kroger: true,
    'H-E-B': true,
    "Trader Joe's": true,
  },
};

function ExperimentalDictionaryLab() {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [resolveForm, setResolveForm] = useState(initialResolveForm);
  const [syncForm, setSyncForm] = useState(initialSyncForm);
  const [seedForm, setSeedForm] = useState(initialSeedForm);
  const [aliasEditForm, setAliasEditForm] = useState(initialAliasEditForm);
  const [bootstrapForm, setBootstrapForm] = useState(initialBootstrapForm);
  const [resolveResult, setResolveResult] = useState(null);
  const [bootstrapResult, setBootstrapResult] = useState(null);
  const [submitting, setSubmitting] = useState({
    resolve: false,
    sync: false,
    seed: false,
    alias: false,
    bootstrap: false,
  });

  const loadStats = async () => {
    try {
      setLoadingStats(true);
      const data = await transactionsAPI.getExperimentalDictionaryStats();
      setStats(data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Failed to load experimental dictionary stats.');
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const setSubmittingFlag = (key, value) => {
    setSubmitting((current) => ({ ...current, [key]: value }));
  };

  const handleResolve = async (event) => {
    event.preventDefault();
    setError('');
    setFeedback('');
    setSubmittingFlag('resolve', true);

    try {
      const items = String(resolveForm.itemsText || '')
        .split('\n')
        .map((rawText) => rawText.trim())
        .filter(Boolean)
        .map((rawText) => ({ rawText }));

      const data = await transactionsAPI.resolveExperimentalDictionaryItems({
        merchant: resolveForm.merchant,
        items,
        limit: 5,
      });
      setResolveResult(data);
      setFeedback(`Resolved ${data.count || 0} item${data.count === 1 ? '' : 's'}.`);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Failed to resolve experimental dictionary items.');
    } finally {
      setSubmittingFlag('resolve', false);
    }
  };

  const handleSync = async (event) => {
    event.preventDefault();
    setError('');
    setFeedback('');
    setSubmittingFlag('sync', true);

    try {
      const data = await transactionsAPI.syncExperimentalDictionaryOpenFoodFacts(syncForm);
      setFeedback(
        `Open Food Facts sync completed. Fetched ${data.fetchedCount || 0} products and inserted ${data.insertedCount || 0} aliases.`
      );
      await loadStats();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Failed to sync Open Food Facts into the experimental dictionary.');
    } finally {
      setSubmittingFlag('sync', false);
    }
  };

  const handleSeed = async (event) => {
    event.preventDefault();
    setError('');
    setFeedback('');
    setSubmittingFlag('seed', true);

    try {
      const data = await transactionsAPI.seedExperimentalDictionary(seedForm);
      setFeedback(`Manual seed inserted ${data.insertedCount || 0} alias variants.`);
      await loadStats();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Failed to seed the experimental dictionary.');
    } finally {
      setSubmittingFlag('seed', false);
    }
  };

  const handleAliasEdit = async (event) => {
    event.preventDefault();
    setError('');
    setFeedback('');
    setSubmittingFlag('alias', true);

    try {
      const data = await transactionsAPI.recordExperimentalAliasEdit(aliasEditForm);
      setFeedback(`Saved user alias edit and inserted ${data.insertedCount || 0} alias variants.`);
      await loadStats();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Failed to save the experimental alias edit.');
    } finally {
      setSubmittingFlag('alias', false);
    }
  };

  const handleBootstrap = async (event) => {
    event.preventDefault();
    setError('');
    setFeedback('');
    setSubmittingFlag('bootstrap', true);

    try {
      const merchants = Object.entries(bootstrapForm.merchants || {})
        .filter(([, enabled]) => Boolean(enabled))
        .map(([merchant]) => merchant);

      const data = await transactionsAPI.bootstrapExperimentalDictionary({
        scope: bootstrapForm.scope,
        includeOpenFoodFacts: bootstrapForm.includeOpenFoodFacts,
        merchants,
      });
      setBootstrapResult(data);
      setFeedback(
        `Starter pack complete. Manual aliases: ${data.manualInsertedCount || 0}. Synced aliases: ${data.syncInsertedCount || 0}.`
      );
      await loadStats();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Failed to bootstrap the experimental dictionary starter pack.');
    } finally {
      setSubmittingFlag('bootstrap', false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 p-6 text-white shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-200/85">Experimental Lab</p>
        <h1 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">Dictionary Alias Testing</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-200/85 sm:text-base">
          This lab hits only the isolated experimental dictionary endpoints. It does not change the live receipt extraction path.
        </p>
      </div>

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      ) : null}
      {feedback ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{feedback}</div>
      ) : null}

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Starter Pack Bootstrap</h2>
        <p className="mt-1 text-sm text-slate-600">
          Seed a useful Costco/Walmart test dictionary in one shot, with optional Open Food Facts enrichment.
        </p>
        <form className="mt-4 space-y-4" onSubmit={handleBootstrap}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Scope</label>
              <select
                value={bootstrapForm.scope}
                onChange={(event) => setBootstrapForm((current) => ({ ...current, scope: event.target.value }))}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              >
                <option value="shared">Shared</option>
                <option value="user">User</option>
              </select>
            </div>
            <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={bootstrapForm.includeOpenFoodFacts}
                onChange={(event) =>
                  setBootstrapForm((current) => ({
                    ...current,
                    includeOpenFoodFacts: event.target.checked,
                  }))
                }
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              Include Open Food Facts sync
            </label>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Merchants</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {Object.keys(bootstrapForm.merchants).map((merchant) => (
                <label
                  key={merchant}
                  className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700"
                >
                  <input
                    type="checkbox"
                    checked={bootstrapForm.merchants[merchant]}
                    onChange={(event) =>
                      setBootstrapForm((current) => ({
                        ...current,
                        merchants: {
                          ...current.merchants,
                          [merchant]: event.target.checked,
                        },
                      }))
                    }
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  {merchant}
                </label>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting.bootstrap}
            className="rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-emerald-300"
          >
            {submitting.bootstrap ? 'Bootstrapping...' : 'Bootstrap Starter Pack'}
          </button>
        </form>

        {bootstrapResult ? (
          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-900">Bootstrap Summary</p>
            <p className="mt-2 text-sm text-slate-700">
              Manual inserted: {bootstrapResult.manualInsertedCount || 0} | Sync fetched: {bootstrapResult.syncFetchedCount || 0} | Sync inserted: {bootstrapResult.syncInsertedCount || 0}
            </p>
            <div className="mt-3 space-y-2 text-sm text-slate-700">
              {(bootstrapResult.merchants || []).map((entry) => (
                <div key={entry.merchant} className="rounded-xl bg-white px-3 py-2 shadow-sm">
                  <span className="font-semibold text-slate-900">{entry.merchant}</span>
                  <span className="ml-2 text-slate-500">
                    manual {entry.manualInsertedCount || 0}, fetched {entry.syncFetchedCount || 0}, sync aliases {entry.syncInsertedCount || 0}
                  </span>
                </div>
              ))}
            </div>
            {(bootstrapResult.syncErrors || []).length ? (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {(bootstrapResult.syncErrors || []).map((entry, index) => (
                  <p key={`${entry.merchant}-${entry.query}-${index}`}>
                    {entry.merchant}: {entry.query} -> {entry.error}
                  </p>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Dictionary Stats</h2>
            <p className="text-sm text-slate-600">Quick health check for the isolated alias dictionary.</p>
          </div>
          <button
            type="button"
            onClick={loadStats}
            className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            Refresh
          </button>
        </div>

        {loadingStats ? (
          <p className="mt-4 text-sm text-slate-600">Loading stats...</p>
        ) : (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Shared Aliases</p>
              <p className="mt-2 text-2xl font-bold text-slate-900">{stats?.sharedCount || 0}</p>
            </div>
            <div className="rounded-2xl bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">User Aliases</p>
              <p className="mt-2 text-2xl font-bold text-slate-900">{stats?.userCount || 0}</p>
            </div>
            <div className="rounded-2xl bg-slate-50 p-4 sm:col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recent Syncs</p>
              <div className="mt-2 space-y-2 text-sm text-slate-700">
                {(stats?.recentSyncs || []).length ? (
                  stats.recentSyncs.map((entry) => (
                    <div key={entry.id} className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                        {entry.status}
                      </span>
                      <span>{entry.query || 'No query'}</span>
                      <span className="text-slate-500">fetched {entry.fetchedCount || 0}</span>
                      <span className="text-slate-500">inserted {entry.insertedCount || 0}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500">No syncs recorded yet.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      <div className="grid gap-6 xl:grid-cols-2">
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Resolve Items</h2>
          <p className="mt-1 text-sm text-slate-600">Test how receipt-like item names match against the experimental alias dictionary.</p>
          <form className="mt-4 space-y-4" onSubmit={handleResolve}>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Merchant</label>
              <input
                value={resolveForm.merchant}
                onChange={(event) => setResolveForm((current) => ({ ...current, merchant: event.target.value }))}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Receipt-Like Items</label>
              <textarea
                value={resolveForm.itemsText}
                onChange={(event) => setResolveForm((current) => ({ ...current, itemsText: event.target.value }))}
                rows={7}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              />
            </div>
            <button
              type="submit"
              disabled={submitting.resolve}
              className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
            >
              {submitting.resolve ? 'Resolving...' : 'Resolve'}
            </button>
          </form>

          {resolveResult ? (
            <div className="mt-5 space-y-3">
              {resolveResult.results?.map((result) => (
                <div key={result.rawText} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">{result.rawText}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    normalized: {result.normalizedRaw || '-'} | expanded: {result.expandedRaw || '-'}
                  </p>
                  <div className="mt-3 space-y-2">
                    {(result.matches || []).length ? (
                      result.matches.map((match) => (
                        <div key={`${result.rawText}-${match.id}`} className="rounded-xl bg-white px-3 py-2 text-sm shadow-sm">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-slate-900">{match.canonicalName}</span>
                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                              score {match.score}
                            </span>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                              {match.scope}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-slate-500">
                            raw key: {match.rawKey} | brand: {match.brand || '-'} | source: {match.source}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500">No matches found.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </section>

        <div className="space-y-6">
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Sync Open Food Facts</h2>
            <p className="mt-1 text-sm text-slate-600">Pull product names into the shared or user-scoped experimental dictionary.</p>
            <form className="mt-4 grid gap-4 sm:grid-cols-2" onSubmit={handleSync}>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Query</label>
                <input
                  value={syncForm.query}
                  onChange={(event) => setSyncForm((current) => ({ ...current, query: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Merchant</label>
                <input
                  value={syncForm.merchant}
                  onChange={(event) => setSyncForm((current) => ({ ...current, merchant: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Scope</label>
                <select
                  value={syncForm.scope}
                  onChange={(event) => setSyncForm((current) => ({ ...current, scope: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                >
                  <option value="shared">Shared</option>
                  <option value="user">User</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Page Size</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={syncForm.pageSize}
                  onChange={(event) => setSyncForm((current) => ({ ...current, pageSize: Number(event.target.value) }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={submitting.sync}
                  className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
                >
                  {submitting.sync ? 'Syncing...' : 'Run Sync'}
                </button>
              </div>
            </form>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Manual Seed</h2>
            <form className="mt-4 grid gap-4 sm:grid-cols-2" onSubmit={handleSeed}>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Canonical Name</label>
                <input
                  value={seedForm.canonicalName}
                  onChange={(event) => setSeedForm((current) => ({ ...current, canonicalName: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Brand</label>
                <input
                  value={seedForm.brand}
                  onChange={(event) => setSeedForm((current) => ({ ...current, brand: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Merchant</label>
                <input
                  value={seedForm.merchant}
                  onChange={(event) => setSeedForm((current) => ({ ...current, merchant: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Scope</label>
                <select
                  value={seedForm.scope}
                  onChange={(event) => setSeedForm((current) => ({ ...current, scope: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                >
                  <option value="shared">Shared</option>
                  <option value="user">User</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={submitting.seed}
                  className="rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:bg-amber-200"
                >
                  {submitting.seed ? 'Seeding...' : 'Seed Dictionary'}
                </button>
              </div>
            </form>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Record User Alias Edit</h2>
            <form className="mt-4 grid gap-4 sm:grid-cols-2" onSubmit={handleAliasEdit}>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Raw Text</label>
                <input
                  value={aliasEditForm.rawText}
                  onChange={(event) => setAliasEditForm((current) => ({ ...current, rawText: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Canonical Name</label>
                <input
                  value={aliasEditForm.canonicalName}
                  onChange={(event) => setAliasEditForm((current) => ({ ...current, canonicalName: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Brand</label>
                <input
                  value={aliasEditForm.brand}
                  onChange={(event) => setAliasEditForm((current) => ({ ...current, brand: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Merchant</label>
                <input
                  value={aliasEditForm.merchant}
                  onChange={(event) => setAliasEditForm((current) => ({ ...current, merchant: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={submitting.alias}
                  className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                >
                  {submitting.alias ? 'Saving...' : 'Save Alias Edit'}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}

export default ExperimentalDictionaryLab;
